import { nip19, getPublicKey, finalizeEvent, nip04 } from 'nostr-tools';
import { encryptSecret, decryptSecret, isEncryptedValue } from './keyEncryption';

const NSEC_STORAGE_KEY = 'bchnostr:nsec-key';
const BUNKER_STORAGE_KEY = 'bchnostr:bunker-config';

// ── NIP-46 Bunker Signer ──────────────────────────────────────────
// Supports bunker:// connection strings from nsec.app, Aegis, etc.

export interface BunkerConfig {
  bunkerPubkey: string;   // The remote signer's pubkey
  userPubkey: string;     // The user's pubkey (from connect response)
  relays: string[];       // Relay(s) to communicate over
  secret?: string;        // Optional auth secret from bunker:// URI
}

export function parseBunkerUrl(bunkerUrl: string): { pubkey: string; relays: string[]; secret?: string } {
  // bunker://<pubkey>?relay=wss://...&relay=wss://...&secret=xxx
  const url = bunkerUrl.trim();
  if (!url.startsWith('bunker://')) throw new Error('Invalid bunker URL — must start with bunker://');
  const withoutScheme = url.slice('bunker://'.length);
  const [pubkeyPart, queryPart] = withoutScheme.split('?');
  if (!pubkeyPart || pubkeyPart.length !== 64) throw new Error('Invalid bunker URL — missing or invalid pubkey');
  const params = new URLSearchParams(queryPart || '');
  const relays = params.getAll('relay').filter(Boolean);
  if (relays.length === 0) throw new Error('Invalid bunker URL — no relay specified');
  const secret = params.get('secret') || undefined;
  return { pubkey: pubkeyPart, relays, secret };
}

export function storeBunkerConfig(config: BunkerConfig): void {
  localStorage.setItem(BUNKER_STORAGE_KEY, JSON.stringify(config));
}

export function loadBunkerConfig(): BunkerConfig | null {
  try {
    const raw = localStorage.getItem(BUNKER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearBunkerConfig(): void {
  localStorage.removeItem(BUNKER_STORAGE_KEY);
}

/**
 * Creates a NIP-46 remote signer that communicates with the bunker
 * over Nostr relays using kind 24133 encrypted messages.
 * Installs itself as window.nostr so all existing code works.
 */
export async function connectBunker(bunkerUrl: string, onProgress?: (msg: string) => void): Promise<BunkerConfig> {
  const { pubkey: bunkerPubkey, relays, secret } = parseBunkerUrl(bunkerUrl);

  // Generate a local ephemeral keypair for this session
  const { generateSecretKey } = await import('nostr-tools');
  const localSecret = generateSecretKey();
  const localPubkey = getPublicKey(localSecret);

  onProgress?.('Connecting to relay...');

  // Connect to the first available relay
  const relay = relays[0];
  const ws = new WebSocket(relay);

  await new Promise<void>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('Relay connection timed out')), 10000);
    ws.onopen = () => { clearTimeout(t); resolve(); };
    ws.onerror = () => { clearTimeout(t); reject(new Error('Failed to connect to relay: ' + relay)); };
  });

  onProgress?.('Sending connect request...');

  // Helper: encrypt a message to the bunker using NIP-04
  const encrypt = (content: string) => nip04.encrypt(localSecret, bunkerPubkey, content);
  const decrypt = (content: string, fromPubkey: string) => nip04.decrypt(localSecret, fromPubkey, content);

  // Subscribe to responses from the bunker
  const subId = Math.random().toString(36).slice(2);
  ws.send(JSON.stringify(['REQ', subId, { kinds: [24133], '#p': [localPubkey], limit: 1 }]));

  // Send connect request
  const connectReqId = Math.random().toString(36).slice(2);
  const connectParams = secret ? [localPubkey, secret, ''] : [localPubkey, '', ''];
  const connectPayload = JSON.stringify({ id: connectReqId, method: 'connect', params: connectParams });
  const encryptedConnect = await encrypt(connectPayload);

  const connectEvent = finalizeEvent({
    kind: 24133,
    created_at: Math.floor(Date.now() / 1000),
    tags: [['p', bunkerPubkey]],
    content: encryptedConnect,
    pubkey: localPubkey,
  } as any, localSecret);

  ws.send(JSON.stringify(['EVENT', connectEvent]));

  // Wait for connect ACK and get_public_key response
  onProgress?.('Waiting for signer approval...');

  const userPubkey = await new Promise<string>((resolve, reject) => {
    const t = setTimeout(() => { ws.close(); reject(new Error('Signer did not respond. Make sure your signer app is open.')); }, 30000);

    ws.onmessage = async (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg[0] !== 'EVENT') return;
        const event = msg[2];
        if (event.kind !== 24133) return;

        const decrypted = await decrypt(event.content, event.pubkey).catch(() => null);
        if (!decrypted) return;

        const response = JSON.parse(decrypted);

        // Connect ACK — now request public key
        if (response.id === connectReqId && (response.result === 'ack' || response.result === 'auth_url')) {
          const getPkReqId = Math.random().toString(36).slice(2);
          const getPkPayload = JSON.stringify({ id: getPkReqId, method: 'get_public_key', params: [] });
          const encryptedGetPk = await encrypt(getPkPayload);
          const getPkEvent = finalizeEvent({
            kind: 24133,
            created_at: Math.floor(Date.now() / 1000),
            tags: [['p', bunkerPubkey]],
            content: encryptedGetPk,
            pubkey: localPubkey,
          } as any, localSecret);
          ws.send(JSON.stringify(['EVENT', getPkEvent]));
          return;
        }

        // get_public_key response
        if (response.result && response.result.length === 64 && /^[0-9a-f]+$/.test(response.result)) {
          clearTimeout(t);
          ws.close();
          resolve(response.result);
        }
      } catch {}
    };
  });

  const config: BunkerConfig = { bunkerPubkey, userPubkey, relays, secret };
  storeBunkerConfig(config);

  // Install persistent bunker signer as window.nostr
  installBunkerSigner(config, localSecret);

  return config;
}

/**
 * Creates and installs a persistent NIP-46 window.nostr signer.
 * Each signing request opens a new relay connection.
 */
export function installBunkerSigner(config: BunkerConfig, localSecretKey?: Uint8Array): void {
  // Generate or reuse ephemeral session key
  let sessionKey: Uint8Array;
  const SESSION_KEY = 'bchnostr:bunker-session-key';

  if (localSecretKey) {
    sessionKey = localSecretKey;
    // Store as hex for reuse
    const hex = Array.from(localSecretKey).map(b => b.toString(16).padStart(2, '0')).join('');
    sessionStorage.setItem(SESSION_KEY, hex);
  } else {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      sessionKey = new Uint8Array(stored.match(/.{2}/g)!.map(b => parseInt(b, 16)));
    } else {
      // Need to reconnect — can't sign without session key
      // This case is handled by restoring session on page load
      return;
    }
  }

  const localPubkey = getPublicKey(sessionKey);
  const { bunkerPubkey, relays } = config;

  const remoteSign = async (method: string, params: any[]): Promise<string> => {
    const relay = relays[0];
    const ws = new WebSocket(relay);
    await new Promise<void>((resolve, reject) => {
      const t = setTimeout(() => reject(new Error('Relay timeout')), 10000);
      ws.onopen = () => { clearTimeout(t); resolve(); };
      ws.onerror = () => { clearTimeout(t); reject(new Error('Relay error')); };
    });

    const reqId = Math.random().toString(36).slice(2);
    const payload = JSON.stringify({ id: reqId, method, params });
    const encrypted = await nip04.encrypt(sessionKey, bunkerPubkey, payload);
    const event = finalizeEvent({
      kind: 24133,
      created_at: Math.floor(Date.now() / 1000),
      tags: [['p', bunkerPubkey]],
      content: encrypted,
      pubkey: localPubkey,
    } as any, sessionKey);

    const subId = Math.random().toString(36).slice(2);
    ws.send(JSON.stringify(['REQ', subId, { kinds: [24133], '#p': [localPubkey], since: Math.floor(Date.now() / 1000) - 5 }]));
    ws.send(JSON.stringify(['EVENT', event]));

    return new Promise<string>((resolve, reject) => {
      const t = setTimeout(() => { ws.close(); reject(new Error('Signer did not respond')); }, 30000);
      ws.onmessage = async (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg[0] !== 'EVENT') return;
          const ev = msg[2];
          if (ev.kind !== 24133) return;
          const decrypted = await nip04.decrypt(sessionKey, ev.pubkey, ev.content).catch(() => null);
          if (!decrypted) return;
          const response = JSON.parse(decrypted);
          if (response.id !== reqId) return;
          clearTimeout(t);
          ws.close();
          if (response.error) reject(new Error(response.error));
          else resolve(response.result);
        } catch {}
      };
    });
  };

  // A bunker signer is app-held too — lock it against extension re-injection.
  installAppSigner({
    getPublicKey: async () => config.userPubkey,
    signEvent: async (event: any) => {
      const eventJson = JSON.stringify(event);
      const result = await remoteSign('sign_event', [eventJson]);
      // Result is the signed event JSON
      try { return JSON.parse(result); } catch { return event; }
    },
    nip04: {
      encrypt: async (recipientPubkey: string, plaintext: string) => {
        return remoteSign('nip04_encrypt', [recipientPubkey, plaintext]);
      },
      decrypt: async (senderPubkey: string, ciphertext: string) => {
        return remoteSign('nip04_decrypt', [senderPubkey, ciphertext]);
      },
    },
    nip44: {
      encrypt: async (recipientPubkey: string, plaintext: string) => {
        return remoteSign('nip44_encrypt', [recipientPubkey, plaintext]);
      },
      decrypt: async (senderPubkey: string, ciphertext: string) => {
        return remoteSign('nip44_decrypt', [senderPubkey, ciphertext]);
      },
    },
  });
}

/**
 * Decode an nsec bech32 string to a hex secret key.
 */
export function decodeNsec(nsec: string): Uint8Array {
  const trimmed = nsec.trim();
  if (!trimmed.startsWith('nsec1')) {
    throw new Error('Invalid nsec: must start with nsec1');
  }
  const decoded = nip19.decode(trimmed);
  if (decoded.type !== 'nsec') {
    throw new Error('Invalid nsec format');
  }
  return decoded.data as Uint8Array;
}

/**
 * Derive the hex public key from a secret key.
 */
export function pubkeyFromSecret(secretKey: Uint8Array): string {
  return getPublicKey(secretKey);
}

// ── nsec storage (optionally PIN-encrypted at rest) ───────────────
//
// Two states can live at NSEC_STORAGE_KEY:
//   · plaintext nsec1…            — the legacy default (still supported)
//   · an encrypted envelope       — when the user has set a PIN
//
// After the user unlocks once per session, the decrypted nsec is held in
// memory (`sessionNsec`) so we don't re-prompt on every action. It is never
// written back to storage in plaintext.

let sessionNsec: string | null = null;

/**
 * Store the nsec. Plaintext by default (unchanged legacy behaviour); if the
 * user has PIN encryption enabled and the session is unlocked, we would have
 * re-encrypted via enableNsecEncryption instead. This base function keeps the
 * plaintext contract that existing callers rely on, and also seeds the
 * session cache.
 */
export function storeNsec(nsec: string): void {
  sessionNsec = nsec;
  localStorage.setItem(NSEC_STORAGE_KEY, nsec);
}

/**
 * Load the stored nsec for immediate (synchronous) use.
 *
 * Returns the plaintext nsec when storage is unencrypted OR when it has
 * already been unlocked this session. Returns null when nothing is stored,
 * OR when an encrypted key exists but hasn't been unlocked yet — callers must
 * treat null-with-`isNsecEncrypted()` as "locked, prompt for PIN" rather than
 * "logged out".
 */
export function loadStoredNsec(): string | null {
  const raw = localStorage.getItem(NSEC_STORAGE_KEY);
  if (!raw) return null;
  if (isEncryptedValue(raw)) {
    // Only available after a successful unlock this session.
    return sessionNsec;
  }
  return raw;
}

/** True when the stored nsec is encrypted behind a PIN. */
export function isNsecEncrypted(): boolean {
  return isEncryptedValue(localStorage.getItem(NSEC_STORAGE_KEY));
}

/**
 * True when we can hand out the nsec right now — i.e. it's plaintext, or it's
 * encrypted AND already unlocked this session.
 */
export function isNsecUnlocked(): boolean {
  const raw = localStorage.getItem(NSEC_STORAGE_KEY);
  if (!raw) return false;
  if (!isEncryptedValue(raw)) return true;
  return sessionNsec !== null;
}

/**
 * Attempt to unlock an encrypted nsec with the PIN. On success the plaintext
 * is cached in memory for the session and returned. Throws on a wrong PIN.
 */
export async function unlockNsec(pin: string): Promise<string> {
  const raw = localStorage.getItem(NSEC_STORAGE_KEY);
  if (!raw) throw new Error('No stored key to unlock.');
  if (!isEncryptedValue(raw)) { sessionNsec = raw; return raw; }
  const plain = await decryptSecret(raw, pin);
  sessionNsec = plain;
  return plain;
}

/**
 * Turn ON PIN encryption for the stored nsec. Requires the plaintext nsec
 * (which the caller has, since the user is logged in). Replaces the plaintext
 * in storage with an encrypted envelope and keeps the session unlocked.
 */
export async function enableNsecEncryption(nsec: string, pin: string): Promise<void> {
  const envelope = await encryptSecret(nsec, pin);
  localStorage.setItem(NSEC_STORAGE_KEY, envelope);
  sessionNsec = nsec; // stay usable for the rest of this session
}

/**
 * Turn OFF PIN encryption, reverting to plaintext storage. Requires the PIN
 * to prove the user can currently decrypt (and to obtain the plaintext).
 */
export async function disableNsecEncryption(pin: string): Promise<void> {
  const raw = localStorage.getItem(NSEC_STORAGE_KEY);
  if (!raw) throw new Error('No stored key.');
  const plain = isEncryptedValue(raw) ? await decryptSecret(raw, pin) : raw;
  localStorage.setItem(NSEC_STORAGE_KEY, plain);
  sessionNsec = plain;
}

/** Change the PIN: decrypt with the old, re-encrypt with the new. */
export async function changeNsecPin(oldPin: string, newPin: string): Promise<void> {
  const raw = localStorage.getItem(NSEC_STORAGE_KEY);
  if (!raw || !isEncryptedValue(raw)) throw new Error('No encrypted key to re-key.');
  const plain = await decryptSecret(raw, oldPin);
  const envelope = await encryptSecret(plain, newPin);
  localStorage.setItem(NSEC_STORAGE_KEY, envelope);
  sessionNsec = plain;
}

export function clearStoredNsec(): void {
  sessionNsec = null;
  localStorage.removeItem(NSEC_STORAGE_KEY);
}

/** Drop the in-memory plaintext (e.g. explicit "lock now") without logging out. */
export function lockNsecSession(): void {
  sessionNsec = null;
}

/**
 * Create a window.nostr-compatible signer object from an nsec.
 * This allows all existing code that uses window.nostr to work
 * without any changes.
 */
export function createNsecSigner(secretKey: Uint8Array) {
  const pubkey = getPublicKey(secretKey);

  return {
    getPublicKey: async () => pubkey,

    signEvent: async (event: any) => {
      const eventWithPubkey = { ...event, pubkey };
      const signed = finalizeEvent(eventWithPubkey, secretKey);
      return signed;
    },

    nip04: {
      encrypt: async (recipientPubkey: string, plaintext: string) => {
        return nip04.encrypt(secretKey, recipientPubkey, plaintext);
      },
      decrypt: async (senderPubkey: string, ciphertext: string) => {
        return nip04.decrypt(secretKey, senderPubkey, ciphertext);
      },
    },

    nip44: {
      encrypt: async (recipientPubkey: string, plaintext: string) => {
        // NIP-44 requires a different implementation, fall back to NIP-04 for now
        return nip04.encrypt(secretKey, recipientPubkey, plaintext);
      },
      decrypt: async (senderPubkey: string, ciphertext: string) => {
        try {
          return await nip04.decrypt(secretKey, senderPubkey, ciphertext);
        } catch {
          // If NIP-04 decrypt fails, the content might be NIP-44 which we can't decode
          throw new Error('NIP-44 decryption not supported with nsec login. Use a Nostr extension for full support.');
        }
      },
    },
  };
}

// ── App-held signer lock ──────────────────────────────────────────
//
// When the active identity is an app-held key (an nsec personal login, or a
// Business Page), we install it as `window.nostr` so every existing call site
// keeps working. But a browser EXTENSION (nos2x / Alby) re-injects its own
// `window.nostr` on every page load — often ASYNCHRONOUSLY, AFTER our restore
// effect has already installed the app signer. That injection silently
// overwrote our signer, so a post made while "acting as" a Business Page was
// signed by the extension (the personal account) and landed on the personal
// profile.
//
// Fix: once we install an app-held signer we LOCK `window.nostr` behind an
// accessor. Reads return our signer; writes (a late extension injection) are
// captured into a side slot instead of replacing it. releaseAppSigner()
// restores plain behaviour and hands back to the extension if one tried to
// inject — used when switching to / restoring an extension personal login,
// and on logout.

let _appSigner: unknown = null;      // the app-held signer we serve
let _shadowedExternal: unknown = null; // an extension's injection, parked aside
let _locked = false;

function lockWindowNostr() {
  if (_locked) return;
  _locked = true;
  try {
    Object.defineProperty(window, 'nostr', {
      configurable: true,
      enumerable: true,
      get() { return _appSigner; },
      set(v) {
        // An extension (or anything else) trying to take over: park it, don't
        // let it replace the app-held signer that the active identity needs.
        _shadowedExternal = v;
      },
    });
  } catch {
    // If the property can't be redefined (already non-configurable), fall
    // back to a plain assignment — better than throwing.
    _locked = false;
    (window as unknown as { nostr: unknown }).nostr = _appSigner;
  }
}

/** Install an app-held signer (nsec / business) and protect it from being
 *  clobbered by a re-injecting extension. */
function installAppSigner(signer: unknown): void {
  _appSigner = signer;
  lockWindowNostr();
}

/**
 * Release the app-held signer lock. Restores `window.nostr` to a normal
 * writable property. If an extension tried to inject while we were locked,
 * hand control back to it; otherwise clear it. Call this when switching to an
 * extension login or on logout.
 */
export function releaseAppSigner(): void {
  const external = _shadowedExternal;
  _appSigner = null;
  _shadowedExternal = null;
  if (_locked) {
    _locked = false;
    try { delete (window as unknown as { nostr?: unknown }).nostr; } catch { /* ignore */ }
  }
  // Restore whatever the extension last tried to inject, if anything.
  if (external) {
    try { (window as unknown as { nostr: unknown }).nostr = external; } catch { /* ignore */ }
  }
}

/**
 * Install the nsec signer as window.nostr so all existing code works.
 * The signer is app-held, so it is LOCKED against extension re-injection.
 */
export function installNsecSigner(secretKey: Uint8Array): string {
  const signer = createNsecSigner(secretKey);
  installAppSigner(signer);
  return pubkeyFromSecret(secretKey);
}

